import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface QuestionnaireDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any;
}

export function QuestionnaireDetailsModal({
  open,
  onOpenChange,
  data,
}: QuestionnaireDetailsModalProps) {
  if (!data) return null;

  const renderSection = (title: string, content: React.ReactNode) => (
    <div className="space-y-2">
      <h4 className="font-semibold text-sm text-muted-foreground uppercase">{title}</h4>
      <div className="pl-4">{content}</div>
      <Separator className="my-4" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Questionário de Primeiro Atendimento</DialogTitle>
          <DialogDescription>
            Dados completos do questionário preenchido pelo paciente
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4">
            {renderSection(
              "Dados Pessoais",
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Nome:</strong> {data.name || 'Não informado'}</div>
                <div><strong>Sexo:</strong> {data.sex || 'Não informado'}</div>
                <div><strong>Data de Nascimento:</strong> {data.birthDate || 'Não informado'}</div>
                <div><strong>Estado Civil:</strong> {data.maritalStatus || 'Não informado'}</div>
                <div><strong>Telefone:</strong> {data.phone || 'Não informado'}</div>
                <div><strong>Plano de Saúde:</strong> {data.healthPlan || 'Não informado'}</div>
                <div><strong>Escolaridade:</strong> {data.education || 'Não informado'}</div>
                <div><strong>Profissão:</strong> {data.profession || 'Não informado'}</div>
                <div><strong>Naturalidade:</strong> {data.birthplace || 'Não informado'}</div>
                <div><strong>Residência:</strong> {data.residence || 'Não informado'}</div>
                <div><strong>CPF:</strong> {data.cpf || 'Não informado'}</div>
              </div>
            )}

            {renderSection(
              "Motivo da Consulta",
              <p className="text-sm whitespace-pre-wrap">{data.mainReason || 'Não informado'}</p>
            )}

            {renderSection(
              "Doenças e Medicamentos",
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Doenças:</strong>
                  <p className="mt-1 whitespace-pre-wrap">{data.diseases || 'Não informado'}</p>
                </div>
                <div>
                  <strong>Medicamentos em uso:</strong>
                  <p className="mt-1 whitespace-pre-wrap">{data.medications || 'Não informado'}</p>
                </div>
              </div>
            )}

            {renderSection(
              "Alergias",
              <p className="text-sm whitespace-pre-wrap">{data.allergies || 'Não informado'}</p>
            )}

            {renderSection(
              "Atividade Física",
              <p className="text-sm">
                {data.physicalActivity === 'sedentary' && 'Sedentário'}
                {data.physicalActivity === 'regular' && 'Regular'}
                {data.physicalActivity === 'performance' && 'Performance'}
                {!data.physicalActivity && 'Não informado'}
              </p>
            )}

            {data.smoking && (
              renderSection(
                "Tabagismo",
                <div className="space-y-2 text-sm">
                  <div><strong>Fumante/Ex-fumante:</strong> {data.smoking.isSmoker ? 'Sim' : 'Não'}</div>
                  {data.smoking.isSmoker && (
                    <>
                      <div><strong>Cigarros por dia:</strong> {data.smoking.cigarettesPerDay || 'Não informado'}</div>
                      <div><strong>Idade que iniciou:</strong> {data.smoking.startedAge || 'Não informado'}</div>
                      {data.smoking.quitDate && (
                        <div><strong>Cessou em:</strong> {data.smoking.quitDate}</div>
                      )}
                    </>
                  )}
                </div>
              )
            )}

            {data.alcohol && (
              renderSection(
                "Consumo de Álcool",
                <div className="space-y-2 text-sm">
                  <div><strong>Consome álcool:</strong> {data.alcohol.consumes ? 'Sim' : 'Não'}</div>
                  {data.alcohol.consumes && (
                    <>
                      <div><strong>Bebida e quantidade:</strong> {data.alcohol.drinkType || 'Não informado'}</div>
                      <div><strong>Frequência:</strong> {data.alcohol.frequency || 'Não informado'}</div>
                      <div><strong>Idade que iniciou:</strong> {data.alcohol.startedAge || 'Não informado'}</div>
                      {data.alcohol.quitDate && (
                        <div><strong>Cessou em:</strong> {data.alcohol.quitDate}</div>
                      )}
                    </>
                  )}
                </div>
              )
            )}

            {data.sex === 'F' && data.gynecological && (
              renderSection(
                "Histórico Ginecológico",
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Gestações:</strong> {data.gynecological.pregnancies || 'Não informado'}</div>
                  <div><strong>Partos vaginais:</strong> {data.gynecological.vaginalBirths || 'Não informado'}</div>
                  <div><strong>Cesáreas:</strong> {data.gynecological.cesareans || 'Não informado'}</div>
                  <div><strong>Abortos espontâneos:</strong> {data.gynecological.miscarriages || 'Não informado'}</div>
                  <div><strong>Idade da menopausa:</strong> {data.gynecological.menopauseAge || 'Não informado'}</div>
                  <div>
                    <strong>Terapia hormonal:</strong>{' '}
                    {data.gynecological.hormoneReplacement ? 'Sim' : 'Não'}
                    {data.gynecological.hormoneReplacement && data.gynecological.hormoneReplacementYears && (
                      <span> ({data.gynecological.hormoneReplacementYears} anos)</span>
                    )}
                  </div>
                </div>
              )
            )}

            {renderSection(
              "Histórico Familiar",
              <p className="text-sm whitespace-pre-wrap">{data.familyHistory || 'Não informado'}</p>
            )}

            {renderSection(
              "Cirurgias e Hospitalizações",
              <p className="text-sm whitespace-pre-wrap">{data.surgeries || 'Não informado'}</p>
            )}

            {data.vaccines && (
              renderSection(
                "Vacinas",
                <div className="space-y-2 text-sm">
                  {data.vaccines.flu?.received && (
                    <div>Gripe (Influenza): {data.vaccines.flu.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.covid19?.received && (
                    <div>Covid-19: {data.vaccines.covid19.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.rsv?.received && (
                    <div>Vírus sincicial respiratório: {data.vaccines.rsv.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.yellowFever?.received && (
                    <div>Febre amarela: {data.vaccines.yellowFever.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.hepatitisB?.received && (
                    <div>Hepatite B: {data.vaccines.hepatitisB.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.pneumococcal?.received && (
                    <div>
                      Pneumocócica: {data.vaccines.pneumococcal.type || 'Tipo não informado'}{' '}
                      ({data.vaccines.pneumococcal.year || 'Ano não informado'})
                    </div>
                  )}
                  {data.vaccines.dtpa?.received && (
                    <div>dT/dTpa: {data.vaccines.dtpa.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.shingles?.received && (
                    <div>Herpes Zoster (Shingrix): {data.vaccines.shingles.year || 'Ano não informado'}</div>
                  )}
                  {data.vaccines.other?.received && (
                    <div>Outra(s): {data.vaccines.other.details || 'Detalhes não informados'}</div>
                  )}
                  {!data.vaccines.flu?.received &&
                    !data.vaccines.covid19?.received &&
                    !data.vaccines.rsv?.received &&
                    !data.vaccines.yellowFever?.received &&
                    !data.vaccines.hepatitisB?.received &&
                    !data.vaccines.pneumococcal?.received &&
                    !data.vaccines.dtpa?.received &&
                    !data.vaccines.shingles?.received &&
                    !data.vaccines.other?.received && (
                      <div className="text-muted-foreground">Nenhuma vacina informada</div>
                    )}
                </div>
              )
            )}

            {renderSection(
              "Outras Informações",
              <p className="text-sm whitespace-pre-wrap">{data.otherInfo || 'Não informado'}</p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

